import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import { ArrowLeft, MoreVertical, Ban, Phone, Video, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatPresence } from '@/utils/presence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline wallpaper style to avoid babel plugin cross-file resolution issue
const WALLPAPER_STYLE = {
  backgroundColor: '#FFF8F0',
  backgroundSize: '200px 200px',
  backgroundRepeat: 'repeat',
};

const showCallToast = () => { toast.info('Voice calls coming soon!'); };
const showVideoToast = () => { toast.info('Video calls coming soon!'); };

export default function ChatWindow({ conversation, currentUser, ws, onConversationUpdate, onBack }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [blockStatus, setBlockStatus] = useState({ is_blocked_by_me: false, has_blocked_me: false, can_message: true });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportMessage, setReportMessage] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const optionsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const token = localStorage.getItem('dharmbook_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (conversation?.id) {
      fetchMessages();
      checkBlockStatus();
      markAsRead();
    }
  }, [conversation?.id]);

  // WebSocket handler
  useEffect(() => {
    if (!ws) return;
    const handler = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message' && data.data.conversation_id === conversation?.id) {
          setMessages(prev => [...prev, data.data]);
          markAsRead();
        } else if (data.type === 'message_deleted' && data.data.for_everyone) {
          setMessages(prev => prev.map(m =>
            m.id === data.data.message_id
              ? { ...m, text: '[Message deleted]', is_deleted_for_everyone: true, media_url: null }
              : m
          ));
        } else if (data.type === 'messages_seen') {
          setMessages(prev => prev.map(m =>
            m.sender_id === currentUser?.id ? { ...m, status: 'seen' } : m
          ));
        } else if (data.type === 'message_reaction' && data.data.conversation_id === conversation?.id) {
          setMessages(prev => prev.map(m =>
            m.id === data.data.message_id ? { ...m, reactions: data.data.reactions } : m
          ));
        } else if (data.type === 'typing' && data.data?.conversation_id === conversation?.id) {
          setIsTyping(true);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2500);
        }
      } catch {}
    };
    ws.addEventListener('message', handler);
    return () => ws.removeEventListener('message', handler);
  }, [ws, conversation?.id, currentUser?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) setShowOptions(false);
    };
    if (showOptions) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptions]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/messages/${conversation.id}`, { headers });
      setMessages(response.data);
    } catch {} finally { setLoading(false); }
  };

  const checkBlockStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/${conversation.other_user_id}/blocked-status`, { headers });
      setBlockStatus(response.data);
    } catch {}
  };

  const markAsRead = async () => {
    try {
      await axios.put(`${API}/conversations/${conversation.id}/read`, {}, { headers });
      onConversationUpdate?.();
    } catch {}
  };

  const handleSendMessage = async (text, mediaUrl, mediaType) => {
    if (!text && !mediaUrl) return;
    try {
      let response;
      if (replyTo) {
        response = await axios.post(`${API}/messages/reply`, {
          conversation_id: conversation.id,
          text: text || '',
          reply_to_id: replyTo.id,
          media_url: mediaUrl,
          media_type: mediaType,
        }, { headers });
        setReplyTo(null);
      } else {
        response = await axios.post(`${API}/messages/send`, {
          conversation_id: conversation.id,
          text: text || '',
          media_url: mediaUrl,
          media_type: mediaType,
        }, { headers });
      }
      setMessages(prev => [...prev, response.data]);
      onConversationUpdate?.();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Cannot send message to this user');
        checkBlockStatus();
      }
    }
  };

  const handleReaction = async (messageId, reaction) => {
    try {
      const res = await axios.post(`${API}/messages/${messageId}/react`, { reaction }, { headers });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: res.data.reactions } : m));
    } catch {}
  };

  const handleDeleteMessage = async (messageId, forEveryone) => {
    try {
      await axios.delete(`${API}/messages/${messageId}?for_everyone=${forEveryone}`, { headers });
      if (forEveryone) {
        setMessages(prev => prev.map(m =>
          m.id === messageId ? { ...m, text: '[Message deleted]', is_deleted_for_everyone: true, media_url: null } : m
        ));
      } else {
        setMessages(prev => prev.filter(m => m.id !== messageId));
      }
      toast.success('Message deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const handleReportMessage = (message) => { setReportMessage(message); setShowReportModal(true); };

  const submitReport = async () => {
    if (!reportReason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      await axios.post(`${API}/messages/report`, { message_id: reportMessage.id, reason: reportReason }, { headers });
      toast.success('Message reported');
      setShowReportModal(false); setReportMessage(null); setReportReason('');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to report'); }
  };

  const handleBlockUser = async () => {
    try {
      if (blockStatus.is_blocked_by_me) {
        await axios.delete(`${API}/users/${conversation.other_user_id}/block`, { headers });
        toast.success('User unblocked');
      } else {
        await axios.post(`${API}/users/${conversation.other_user_id}/block`, {}, { headers });
        toast.success('User blocked');
      }
      checkBlockStatus(); setShowOptions(false);
    } catch (error) { toast.error(error.response?.data?.detail || 'Action failed'); }
  };

  const canMessage = blockStatus.can_message;

  return (
    <div className="flex flex-col flex-1 h-full" data-testid="chat-window">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-[#FF9933] to-[#FF7700] text-white flex-shrink-0 shadow-sm z-30">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1 rounded-full hover:bg-white/10" data-testid="chat-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="relative">
            <img
              src={conversation.other_user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.other_user_name || 'U')}&background=FF9933&color=fff&size=40`}
              alt=""
              className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
            />
            {conversation.other_user_online && (
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-[#FF9933]" />
            )}
          </div>
          <div>
            <h2 className="font-semibold text-[15px] leading-tight" data-testid="chat-other-user-name">{conversation.other_user_name}</h2>
            <p className="text-[11px] text-orange-100" data-testid="chat-presence-line">
              {isTyping
                ? 'typing...'
                : (formatPresence({
                    is_online: conversation.other_user_online,
                    last_seen: conversation.other_user_last_seen,
                    show_last_seen: conversation.other_user_show_last_seen,
                  }) || 'DharmBook Member')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={showCallToast}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
            data-testid="call-btn"
          >
            <Phone className="w-[18px] h-[18px]" />
          </button>
          <button
            onClick={showVideoToast}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
            data-testid="video-btn"
          >
            <Video className="w-[18px] h-[18px]" />
          </button>
          <div className="relative" ref={optionsRef}>
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              data-testid="chat-options-btn"
            >
              <MoreVertical className="w-[18px] h-[18px]" />
            </button>
            {showOptions && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-slate-200 py-1 min-w-[180px] z-50">
                <button
                  onClick={handleBlockUser}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                  data-testid="block-user-btn"
                >
                  <Ban className="w-4 h-4" />
                  {blockStatus.is_blocked_by_me ? 'Unblock User' : 'Block User'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Blocked notice */}
      {!canMessage && (
        <div className="bg-red-50 border-b border-red-100 px-4 py-2 text-center">
          <p className="text-xs text-red-600">{blockStatus.has_blocked_me ? 'You cannot message this user' : 'You have blocked this user'}</p>
        </div>
      )}

      {/* Messages with spiritual wallpaper */}
      <div
        className="flex-1 overflow-y-auto px-3 py-3"
        data-testid="messages-container"
        style={WALLPAPER_STYLE}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-2 animate-pulse">
                <span className="text-[#FF9933] text-lg" style={{ fontFamily: 'serif' }}>Om</span>
              </div>
              <p className="text-xs text-slate-400">Loading messages...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-6 py-5 text-center shadow-sm border border-orange-100">
              <div className="text-3xl mb-2" style={{ fontFamily: 'serif', color: '#FF9933' }}>Om</div>
              <p className="text-slate-600 text-sm font-medium">Start a blessed conversation</p>
              <p className="text-xs text-slate-400 mt-1">Send a peaceful message</p>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isMine={message.sender_id === currentUser?.id || message.is_own_message}
                currentUserId={currentUser?.id}
                onDelete={handleDeleteMessage}
                onReport={handleReportMessage}
                onReact={handleReaction}
                onReply={() => setReplyTo(message)}
              />
            ))}
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      <MessageInput
        onSend={handleSendMessage}
        disabled={!canMessage}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        ws={ws}
        conversationId={conversation?.id}
      />

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Report Message</h3>
              <button onClick={() => { setShowReportModal(false); setReportMessage(null); setReportReason(''); }} className="p-1 rounded-full hover:bg-slate-100">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="mb-4 p-3 bg-slate-100 rounded-xl">
              <p className="text-sm text-slate-600 italic">"{reportMessage?.text}"</p>
            </div>
            <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-300 mb-4" data-testid="report-reason-select">
              <option value="">Select a reason</option>
              <option value="harassment">Harassment or bullying</option>
              <option value="hate_speech">Hate speech</option>
              <option value="inappropriate">Inappropriate content</option>
              <option value="spam">Spam</option>
              <option value="other">Other</option>
            </select>
            <div className="flex gap-3">
              <button onClick={() => { setShowReportModal(false); setReportMessage(null); setReportReason(''); }} className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm">Cancel</button>
              <button onClick={submitReport} className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 text-sm" data-testid="submit-report-btn">Report</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
